import { useState } from "react";
import { toast } from "sonner";
import { getImageUrl, uploadImageHelper } from "@/lib/api";

const uploadImage = uploadImageHelper;

export const GalleryEditor = ({ gallery, onChange, mainImage, onSetMain }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [newUrl, setNewUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const moveImage = (from, to) => {
    if (from === to || from == null || to == null) return;
    const list = [...gallery];
    const [item] = list.splice(from, 1);
    list.splice(to, 0, item);
    onChange(list);
  };

  const removeImage = (i) => onChange(gallery.filter((_, idx) => idx !== i));

  const addUrl = () => {
    const url = newUrl.trim();
    if (!url) return;
    onChange([...gallery, url]);
    setNewUrl("");
  };

  const onGalleryFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        uploaded.push(await uploadImage(file));
      }
      onChange([...gallery, ...uploaded]);
      toast.success(`${files.length} imagen(es) subida(s) a la galería`);
    } catch {
      toast.error("Para añadir imágenes en hosting estático, introduce la URL directa en el campo inferior.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-xs uppercase tracking-widest font-semibold">Galería</p>
      <label className="cursor-pointer text-[#D94A1E] uppercase tracking-wide text-sm font-medium hover:translate-x-1 transition-transform inline-block">
        {uploading ? "Subiendo…" : "Subir imágenes"}
        <input data-testid="project-form-gallery-files" type="file" accept="image/*" multiple className="hidden" onChange={onGalleryFiles} disabled={uploading} />
      </label>
      {gallery.length > 0 && (
        <div className="flex flex-wrap gap-4" data-testid="project-form-gallery-list">
          {gallery.map((url, i) => (
            <GalleryThumb
              key={`${url}-${i}`}
              url={url}
              index={i}
              isMain={mainImage === url}
              isDragging={dragIndex === i}
              isOver={overIndex === i && dragIndex !== null && dragIndex !== i}
              onDragStart={() => setDragIndex(i)}
              onDragOver={() => setOverIndex(i)}
              onDragLeave={() => setOverIndex(null)}
              onDrop={() => { moveImage(dragIndex, i); setDragIndex(null); setOverIndex(null); }}
              onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
              onRemove={() => removeImage(i)}
              onSetMain={() => onSetMain(url)}
            />
          ))}
        </div>
      )}
      {gallery.length > 1 && (
        <p className="text-xs font-light text-[#111111]/50">Arrastra las imágenes para reordenarlas.</p>
      )}
      <div className="flex gap-4 items-end">
        <input
          data-testid="project-form-gallery-url"
          className="ed-input"
          placeholder="Añadir URL de imagen"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
        />
        <button type="button" data-testid="project-form-gallery-add" onClick={addUrl} className="text-sm uppercase tracking-widest font-medium text-[#D94A1E] pb-3 shrink-0">
          Añadir
        </button>
      </div>
    </div>
  );
};

const GalleryThumb = ({ url, index, isMain, isDragging, isOver, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd, onRemove, onSetMain }) => (
  <div
    data-testid={`gallery-thumb-${index}`}
    draggable
    onDragStart={(e) => { onDragStart(); e.dataTransfer.effectAllowed = "move"; }}
    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; onDragOver(); }}
    onDragLeave={onDragLeave}
    onDrop={(e) => { e.preventDefault(); onDrop(); }}
    onDragEnd={onDragEnd}
    className={`relative w-28 cursor-grab active:cursor-grabbing select-none transition-all duration-200 ${isDragging ? "opacity-40" : ""} ${isOver ? "outline outline-2 outline-[#D94A1E] outline-offset-2" : ""}`}
  >
    <img src={getImageUrl(url)} alt={`Galería ${index + 1}`} className="w-28 aspect-square object-cover pointer-events-none" />
    <span className="absolute bottom-1 left-1 text-[10px] font-semibold bg-[#111111] text-[#F3F0EA] px-1.5 py-0.5">{index + 1}</span>
    {isMain ? (
      <span
        data-testid={`gallery-main-badge-${index}`}
        className="absolute bottom-1 right-1 text-[10px] font-semibold bg-[#D94A1E] text-[#F3F0EA] px-1.5 py-0.5 uppercase tracking-wider"
      >
        Principal
      </span>
    ) : (
      <button
        type="button"
        data-testid={`gallery-set-main-${index}`}
        onClick={onSetMain}
        aria-label="Usar como imagen principal"
        title="Usar como imagen principal"
        className="absolute bottom-1 right-1 text-[10px] font-semibold bg-[#F3F0EA] text-[#111111] px-1.5 py-0.5 uppercase tracking-wider hover:bg-[#D94A1E] hover:text-[#F3F0EA] transition-colors"
      >
        Principal
      </button>
    )}
    <button
      type="button"
      data-testid={`gallery-remove-${index}`}
      onClick={onRemove}
      aria-label="Eliminar imagen"
      className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-[#111111] text-[#F3F0EA] text-sm hover:bg-[#D94A1E] transition-colors"
    >
      ×
    </button>
  </div>
);
